// frontend/src/pages/technician/TechnicianInstallations.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Alert,
    CircularProgress,
    Grid,
    TextField,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Fab,
    Badge,
    Divider,
    Paper,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import {
    Assignment,
    Phone,
    Navigation,
    PhotoCamera,
    CheckCircle,
    Schedule,
    Person,
    LocationOn,
    Build,
    Refresh,
    FilterList,
    Route,
    Info
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { technicianApiService } from '../../services/technicianApiService';

const TechnicianInstallations = () => {
    const [installations, setInstallations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [filters, setFilters] = useState({
        status: '',
        date: null
    });
    const [filterDialog, setFilterDialog] = useState(false);
    const [selectedInstallation, setSelectedInstallation] = useState(null);
    const [detailDialog, setDetailDialog] = useState(false);
    const [error, setError] = useState(null);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 20,
        offset: 0
    });

    // Estados para diálogos de Rechazo y Posponer
    const [actionDialog, setActionDialog] = useState(false);
    const [actionType, setActionType] = useState(''); // 'reject' o 'postpone'
    const [actionNotes, setActionNotes] = useState('');
    const [targetInstallationId, setTargetInstallationId] = useState(null);
    const [submittingAction, setSubmittingAction] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        loadInstallations();
    }, [filters, pagination.offset]);

    const loadInstallations = async () => {
        try {
            setLoading(true);
            const params = {
                limit: pagination.limit,
                offset: pagination.offset
            };

            if (filters.status) params.status = filters.status;
            if (filters.date) params.date = filters.date.toISOString().split('T')[0];

            const data = await technicianApiService.getInstallations(params);
            setInstallations(data.installations);
            setPagination(prev => ({
                ...prev,
                total: data.total
            }));
            setError(null);
        } catch (error) {
            console.error('Error cargando instalaciones:', error);
            setError('Error cargando instalaciones');
        } finally {
            setLoading(false);
        }
    };

    const refreshData = async () => {
        try {
            setRefreshing(true);
            await loadInstallations();
        } catch (error) {
            console.error('Error refrescando datos:', error);
        } finally {
            setRefreshing(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPagination(prev => ({ ...prev, offset: 0 }));
    };

    const clearFilters = () => {
        setFilters({
            status: '',
            date: null
        });
        setPagination(prev => ({ ...prev, offset: 0 }));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'in_progress': return 'info';
            case 'completed': return 'success';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'pending': return 'Pendiente';
            case 'in_progress': return 'En Progreso';
            case 'completed': return 'Completada';
            case 'cancelled': return 'Cancelada';
            default: return status;
        }
    };

    // Estados para la captura GPS de alta precisión
    const [gpsLoading, setGpsLoading] = useState(false);
    const [gpsAccuracy, setGpsAccuracy] = useState(null);
    const [gpsStatusText, setGpsStatusText] = useState('');

    const handleStatusUpdate = async (installationId, newStatus) => {
        try {
            setUpdatingStatusId(installationId);
            setError(null);
            
            let extraData = {};
            if (newStatus === 'in_progress') {
                console.log('📍 Capturando GPS de alta precisión al iniciar instalación...');
                if (navigator.geolocation) {
                    setGpsLoading(true);
                    setGpsAccuracy(null);
                    setGpsStatusText('Buscando satélites GPS...');
                    
                    try {
                        const position = await new Promise((resolve, reject) => {
                            let watchId = null;
                            let bestPosition = null;
                            const timeoutId = setTimeout(() => {
                                if (watchId !== null) {
                                    navigator.geolocation.clearWatch(watchId);
                                }
                                if (bestPosition && bestPosition.coords.accuracy < 100) {
                                    console.log('⏰ GPS Timeout: usando mejor posición obtenida:', bestPosition.coords.accuracy);
                                    resolve(bestPosition);
                                } else {
                                    reject(new Error('No se pudo obtener una precisión GPS aceptable en 15 segundos.'));
                                }
                            }, 15000);

                            watchId = navigator.geolocation.watchPosition(
                                (pos) => {
                                    const accuracy = pos.coords.accuracy;
                                    setGpsAccuracy(accuracy);
                                    setGpsStatusText('Estabilizando señal GPS...');
                                    
                                    if (!bestPosition || accuracy < bestPosition.coords.accuracy) {
                                        bestPosition = pos;
                                    }
                                    
                                    if (accuracy <= 30) {
                                        clearTimeout(timeoutId);
                                        navigator.geolocation.clearWatch(watchId);
                                        resolve(pos);
                                    }
                                },
                                (err) => {
                                    clearTimeout(timeoutId);
                                    if (watchId !== null) {
                                        navigator.geolocation.clearWatch(watchId);
                                    }
                                    reject(err);
                                },
                                {
                                    enableHighAccuracy: true,
                                    maximumAge: 0,
                                    timeout: 15000
                                }
                            );
                        });
                        
                        extraData.start_coordinates = `${position.coords.latitude}, ${position.coords.longitude}`;
                        console.log('📍 GPS de inicio capturado con precisión:', position.coords.accuracy, 'm - coords:', extraData.start_coordinates);
                    } catch (gpsError) {
                        console.error('Error GPS al iniciar:', gpsError);
                        alert('Atención: No se pudo obtener la ubicación GPS precisa. Asegúrese de activar el GPS, permitir el acceso y estar a cielo abierto.');
                        throw new Error('La ubicación GPS es obligatoria para iniciar la instalación.');
                    } finally {
                        setGpsLoading(false);
                    }
                } else {
                    throw new Error('Su dispositivo/navegador no soporta geolocalización.');
                }
            }
            
            await technicianApiService.updateInstallationStatus(installationId, newStatus, extraData);
            await refreshData();
        } catch (error) {
            console.error('Error actualizando estado:', error);
            setError(error.message || 'Error actualizando estado de instalación');
        } finally {
            setUpdatingStatusId(null);
            setGpsLoading(false);
        }
    };

    const handleActionSubmit = async () => {
        if (!actionNotes.trim()) {
            alert('Por favor ingrese un motivo');
            return;
        }

        try {
            setSubmittingAction(true);
            setError(null);

            if (actionType === 'reject') {
                await technicianApiService.rejectInstallation(targetInstallationId, actionNotes);
                alert('Instalación rechazada. El administrador revisará la solicitud.');
            } else if (actionType === 'postpone') {
                await technicianApiService.updateInstallationStatus(targetInstallationId, 'pending', { comments: `Pospuesta: ${actionNotes}` });
                alert('Instalación pospuesta. El estado ha regresado a Pendiente.');
            }

            setActionDialog(false);
            setActionNotes('');
            setTargetInstallationId(null);
            await refreshData();
        } catch (err) {
            console.error('Error al procesar acción:', err);
            setError(err.message || 'Error al procesar la solicitud');
        } finally {
            setSubmittingAction(false);
        }
    };

    const showInstallationDetails = async (installation) => {
        try {
            const details = await technicianApiService.getInstallationDetails(installation.id);
            setSelectedInstallation(details);
            setDetailDialog(true);
        } catch (error) {
            console.error('Error cargando detalles:', error);
            setError('Error cargando detalles de instalación');
        }
    };

    const makePhoneCall = (phoneNumber) => {
        window.open(`tel:${phoneNumber}`);
    };

    const openNavigation = (coordinates) => {
        if (coordinates) {
            const coords = coordinates.replace(/[()]/g, '').split(',');
            if (coords.length === 2) {
                const val1 = parseFloat(coords[0].trim());
                const val2 = parseFloat(coords[1].trim());
                let lat = val1;
                let lng = val2;
                if (Math.abs(val1) > 90 && Math.abs(val2) <= 90) {
                    lat = val2;
                    lng = val1;
                }
                window.open(`https://maps.google.com/maps?q=${lat},${lng}`, '_blank');
            }
        }
    };

    const getRouteOptimization = async () => {
        try {
            const route = await technicianApiService.getTodayRoute();
            // Aquí se podría abrir una nueva página con la ruta optimizada
            console.log('Ruta optimizada:', route);
        } catch (error) {
            console.error('Error obteniendo ruta:', error);
        }
    };

    if (loading && installations.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h5" gutterBottom>
                            Mis Instalaciones
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Total: {pagination.total} instalaciones
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                        <Button
                            variant="outlined"
                            startIcon={<FilterList />}
                            onClick={() => setFilterDialog(true)}
                            color={filters.status || filters.date ? 'primary' : 'inherit'}
                        >
                            Filtros
                            {(filters.status || filters.date) && (
                                <Badge color="primary" variant="dot" sx={{ ml: 1 }} />
                            )}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Route />}
                            onClick={getRouteOptimization}
                        >
                            Ruta del Día
                        </Button>
                        <IconButton onClick={refreshData} disabled={refreshing}>
                            <Refresh />
                        </IconButton>
                    </Box>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Lista de instalaciones */}
            {installations.length > 0 ? (
                <Grid container spacing={2}>
                    {installations.map((installation) => (
                        <Grid item xs={12} md={6} lg={4} key={installation.id}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    {/* Header de la tarjeta */}
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                                <Assignment />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" noWrap>
                                                    {installation.first_name} {installation.last_name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {new Date(installation.installation_date).toLocaleDateString('es-PE')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Chip
                                            label={getStatusText(installation.status)}
                                            color={getStatusColor(installation.status)}
                                            size="small"
                                        />
                                    </Box>

                                    {/* Información del cliente */}
                                    <Box mb={2}>
                                        <Typography variant="body2" gutterBottom>
                                            📍 {installation.address}
                                        </Typography>
                                        <Typography variant="body2" color="primary" gutterBottom>
                                            📶 {installation.plan_name} - S/. {installation.plan_price}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            🏷️ {installation.zone_name}
                                        </Typography>
                                    </Box>

                                    {/* Información técnica */}
                                    {installation.router_name && (
                                        <Box mb={2}>
                                            <Typography variant="caption" color="textSecondary">
                                                Router: {installation.router_name}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 2 }} />

                                    {/* Acciones */}
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Box display="flex" gap={1}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => makePhoneCall(installation.phone)}
                                                title="Llamar"
                                            >
                                                <Phone />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="success"
                                                onClick={() => openNavigation(installation.coordinates)}
                                                title="Navegar"
                                                disabled={!installation.coordinates}
                                            >
                                                <Navigation />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="info"
                                                onClick={() => showInstallationDetails(installation)}
                                                title="Ver detalles"
                                            >
                                                <Info />
                                            </IconButton>
                                        </Box>

                                        {/* Botones de estado */}
                                        {installation.status === 'pending' && (
                                            <Box display="flex" gap={1}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => {
                                                        setTargetInstallationId(installation.id);
                                                        setActionType('reject');
                                                        setActionNotes('');
                                                        setActionDialog(true);
                                                    }}
                                                >
                                                    Rechazar
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleStatusUpdate(installation.id, 'in_progress')}
                                                    disabled={updatingStatusId === installation.id}
                                                >
                                                    {updatingStatusId === installation.id ? 'Iniciando...' : 'Iniciar'}
                                                </Button>
                                            </Box>
                                        )}
                                        {installation.status === 'in_progress' && (
                                            <Box display="flex" gap={1}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    onClick={() => {
                                                        setTargetInstallationId(installation.id);
                                                        setActionType('postpone');
                                                        setActionNotes('');
                                                        setActionDialog(true);
                                                    }}
                                                >
                                                    Posponer
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="success"
                                                    onClick={() => navigate(`/technician/installations/${installation.id}/complete`)}
                                                >
                                                    Completar
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Indicador de fotos */}
                                    {installation.photo_count > 0 && (
                                        <Box display="flex" alignItems="center" mt={1}>
                                            <PhotoCamera sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="textSecondary">
                                                {installation.photo_count} foto{installation.photo_count !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Alert severity="info">
                    No se encontraron instalaciones con los filtros aplicados
                </Alert>
            )}

            {/* Paginación */}
            {pagination.total > pagination.limit && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Button
                        disabled={pagination.offset === 0}
                        onClick={() => setPagination(prev => ({
                            ...prev,
                            offset: Math.max(0, prev.offset - prev.limit)
                        }))}
                    >
                        Anterior
                    </Button>
                    <Typography sx={{ mx: 2, alignSelf: 'center' }}>
                        {Math.floor(pagination.offset / pagination.limit) + 1} de{' '}
                        {Math.ceil(pagination.total / pagination.limit)}
                    </Typography>
                    <Button
                        disabled={pagination.offset + pagination.limit >= pagination.total}
                        onClick={() => setPagination(prev => ({
                            ...prev,
                            offset: prev.offset + prev.limit
                        }))}
                    >
                        Siguiente
                    </Button>
                </Box>
            )}

            {/* Dialog de filtros */}
            <Dialog open={filterDialog} onClose={() => setFilterDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Filtrar Instalaciones</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Estado</InputLabel>
                            <Select
                                value={filters.status}
                                label="Estado"
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="pending">Pendiente</MenuItem>
                                <MenuItem value="in_progress">En Progreso</MenuItem>
                                <MenuItem value="completed">Completada</MenuItem>
                                <MenuItem value="cancelled">Cancelada</MenuItem>
                            </Select>
                        </FormControl>

                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                            <DatePicker
                                label="Fecha de instalación"
                                value={filters.date}
                                onChange={(date) => handleFilterChange('date', date)}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </LocalizationProvider>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={clearFilters}>Limpiar</Button>
                    <Button onClick={() => setFilterDialog(false)} variant="contained">
                        Aplicar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de detalles */}
            <Dialog
                open={detailDialog}
                onClose={() => setDetailDialog(false)}
                maxWidth="md"
                fullWidth
            >
                {selectedInstallation && (
                    <>
                        <DialogTitle>
                            Detalles de Instalación
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>
                                        Información del Cliente
                                    </Typography>
                                    <Typography><strong>Nombre:</strong> {selectedInstallation.installation.first_name} {selectedInstallation.installation.last_name}</Typography>
                                    <Typography><strong>Teléfono:</strong> {selectedInstallation.installation.phone}</Typography>
                                    <Typography><strong>Email:</strong> {selectedInstallation.installation.email}</Typography>
                                    <Typography><strong>Dirección:</strong> {selectedInstallation.installation.address}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>
                                        Información Técnica
                                    </Typography>
                                    <Typography><strong>Plan:</strong> {selectedInstallation.installation.plan_name}</Typography>
                                    <Typography><strong>Velocidad:</strong> {selectedInstallation.installation.download_speed}/{selectedInstallation.installation.upload_speed} Mbps</Typography>
                                    <Typography><strong>Router:</strong> {selectedInstallation.installation.router_name}</Typography>
                                    <Typography><strong>IP:</strong> {selectedInstallation.installation.ip_address}</Typography>
                                </Grid>
                            </Grid>

                            {selectedInstallation.photos.length > 0 && (
                                <Box mt={3}>
                                    <Typography variant="h6" gutterBottom>
                                        Fotos ({selectedInstallation.photos.length})
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {selectedInstallation.photos.map((photo) => (
                                            <Grid item xs={6} md={4} key={photo.id}>
                                                <img
                                                    src={photo.photo_url}
                                                    alt={photo.caption}
                                                    style={{ width: '100%', borderRadius: 4 }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDetailDialog(false)}>
                                Cerrar
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
            
            {/* Dialog para captura GPS de alta precisión */}
            <Dialog open={gpsLoading} disableEscapeKeyDown>
                <DialogTitle>📍 Obteniendo Ubicación GPS Precisa</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, minWidth: 280 }}>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="body1" align="center" gutterBottom>
                        {gpsStatusText}
                    </Typography>
                    {gpsAccuracy !== null && (
                        <Typography variant="body2" color={gpsAccuracy <= 30 ? "success.main" : "warning.main"} fontWeight="bold">
                            Precisión actual: {gpsAccuracy.toFixed(1)}m (Meta: ≤ 30m)
                        </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary" align="center" sx={{ mt: 2 }}>
                        Por favor, espere a la intemperie o cerca de una ventana para una captura rápida.
                    </Typography>
                </DialogContent>
            </Dialog>

            {/* Dialog para Rechazo o Posposición */}
            <Dialog open={actionDialog} onClose={() => !submittingAction && setActionDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: actionType === 'reject' ? 'error.main' : 'warning.main' }}>
                    {actionType === 'reject' ? 'Rechazar Instalación' : 'Posponer Instalación'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            {actionType === 'reject' 
                                ? 'Esta acción enviará una solicitud de rechazo/cancelación al administrador. Ingrese el motivo por el cual el cliente no desea continuar o no se puede realizar la instalación.' 
                                : 'Esta acción regresará la instalación al estado "Pendiente" y la quitará de tu jornada en progreso. Por favor, registre la razón o incidente técnico por el cual se pospone.'}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={actionType === 'reject' ? 'Motivo del Rechazo *' : 'Razón/Incidente para Posponer *'}
                            value={actionNotes}
                            onChange={(e) => setActionNotes(e.target.value)}
                            placeholder={actionType === 'reject' ? 'Ej. El cliente indica que ya no cuenta con presupuesto.' : 'Ej. Torre sin energía / Falta de material cpe.'}
                            disabled={submittingAction}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setActionDialog(false)} disabled={submittingAction}>
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleActionSubmit} 
                        variant="contained" 
                        color={actionType === 'reject' ? 'error' : 'warning'}
                        disabled={submittingAction}
                    >
                        {submittingAction 
                            ? 'Procesando...' 
                            : (actionType === 'reject' ? 'Confirmar Rechazo' : 'Confirmar Posponer')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* FAB para crear nueva instalación (si tiene permisos) */}
            <Fab
                color="primary"
                sx={{ position: 'fixed', bottom: 16, right: 16 }}
                onClick={() => navigate('/technician/installations/new')}
            >
                <Assignment />
            </Fab>
        </Box>
    );
};

export default TechnicianInstallations;